import { createContext, useRef, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
// firestore
import { addDoc, collection, deleteDoc, doc, onSnapshot, query, setDoc, orderBy } from "firebase/firestore";
import { db } from "../services/firebase";
// actions
import actionTypes from "../common/actionTypes";
// context
import useAuthContext from "../hooks/useAuthContext";
import useShopContext from "../hooks/useShopContext";
import useActivityLogsContext from "../hooks/useActivityLogsContext";
// reducers
import { settingReducer } from "../reducers/settingsReducer";
import { getUnixTime } from "date-fns";
// hooks
import useCustomReducer from "../hooks/useCustomReducer";
// constants
import {
  DATABASE_STATUTES,
  defaultPOSettings,
  defaultBISSettings,
  defaultCSSettings,
  defaultISSettings,
  SETTINGS_TYPE,
  APP_ROUTES,
} from "../common/constants";
import { LOG_TYPES } from "./ActivityLogsContext";

const SHOPS = "Shops";
const SETTINGS = "Settings";

const initialState = {
  data: [],
  status: DATABASE_STATUTES.NOT_LOADED,
  error: null,
};

const SettingsContext = createContext({
  ...initialState,
  fetchSettings: () => {},
  createSetting: () => Promise.resolve(),
  deleteSetting: () => Promise.resolve(),
  editSetting: () => Promise.resolve(),
  getSettingById: () => Promise.resolve(),
  copySetting: () => Promise.resolve(),
});

const SettingsProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(settingReducer, initialState);

  const settingsRef = useRef(null);

  const {
    shop: { hasSellingPlanEnabled },
  } = useShopContext();
  const { user } = useAuthContext();
  const { writeLog } = useActivityLogsContext();

  const { pathname } = useLocation();
  const fetchingIsActive =
    pathname.startsWith(APP_ROUTES.PRODUCTS.ROOT) ||
    pathname.startsWith(APP_ROUTES.SETTINGS.TEMPLATES) ||
    pathname.startsWith(APP_ROUTES.AUTOMATIONS.TAGS);

  useEffect(() => {
    if (user?.domain && fetchingIsActive) {
      dispatch({
        type: actionTypes.FETCH_SETTINGS,
      });

      settingsRef.current = collection(db, SHOPS, user.domain, SETTINGS);

      try {
        return onSnapshot(query(settingsRef.current, orderBy("createdAt", "asc")), (snapshot) => {
          dispatch({
            type: actionTypes.FETCH_SETTINGS_SUCCESS,
            payload: snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            })),
          });
        });
      } catch (error) {
        dispatch({
          type: actionTypes.FETCH_SETTINGS_FAILURE,
          payload: error,
        });
      }
    }
  }, [user?.domain, fetchingIsActive, dispatch]);

  const getSettingById = useCallback(
    (id) => {
      return state.data.find((elem) => elem.id === id);
    },
    [state.data]
  );

  const updateSettingsLastUpdate = useCallback(async () => {
    await setDoc(doc(db, SHOPS, user.domain), { lastSettingsUpdate: getUnixTime(new Date()) }, { merge: true });
  }, [user?.domain]);

  const createSetting = useCallback(
    async ({ name, type }) => {
      let setting = {
        name: name,
        productsCount: 0,
        createdAt: Date.now(),
        type: type,
      };

      switch (type) {
        case SETTINGS_TYPE.PO:
          setting = {
            ...setting,
            ...defaultPOSettings,
            cart: {
              ...defaultPOSettings.cart,
              labelText: { ...defaultPOSettings.cart.labelText, isEnabled: !hasSellingPlanEnabled },
            },
          };
          break;
        case SETTINGS_TYPE.BIS:
          setting = { ...setting, ...defaultBISSettings };
          break;
        case SETTINGS_TYPE.CS:
          setting = { ...setting, ...defaultCSSettings };
          break;
        case SETTINGS_TYPE.IS:
          setting = { ...setting, ...defaultISSettings };
          break;
        default:
          setting = {
            ...setting,
            ...defaultPOSettings,
            cart: {
              ...defaultPOSettings.cart,
              labelText: { ...defaultPOSettings.cart.labelText, isEnabled: hasSellingPlanEnabled ? false : true },
            },
          };
          break;
      }

      const settingRef = await addDoc(settingsRef.current, setting);

      await updateSettingsLastUpdate();
      await writeLog(LOG_TYPES.ADD_TEMPLATE, name);

      return settingRef.id;
    },
    [hasSellingPlanEnabled, updateSettingsLastUpdate, writeLog]
  );

  const deleteSetting = useCallback(
    async ({ id }) => {
      await deleteDoc(doc(settingsRef.current, id));
      await updateSettingsLastUpdate();

      const template = getSettingById(id);
      await writeLog(LOG_TYPES.DELETE_TEMPLATE, template.name);
    },
    [getSettingById, updateSettingsLastUpdate, writeLog]
  );

  const editSetting = useCallback(
    async ({ id, data, type, name }) => {
      try {
        await setDoc(doc(settingsRef.current, id), { name, [type]: data }, { merge: true });

        await updateSettingsLastUpdate();

        await writeLog(LOG_TYPES.UPDATE_TEMPLATE, name);
      } catch (error) {
        console.log("%c [Error]", "color: red; font-weight: bold;", error);
      }
    },
    [updateSettingsLastUpdate, writeLog]
  );

  const copySetting = useCallback(
    async (targetSettingId) => {
      const targetSetting = getSettingById(targetSettingId);

      const newSettingRef = await addDoc(settingsRef.current, {
        ...targetSetting,
        createdAt: Date.now(),
        name: `${targetSetting.name} copy`,
      });

      await updateSettingsLastUpdate();
      return newSettingRef.id;
    },
    [getSettingById, updateSettingsLastUpdate]
  );

  const memoizedState = useMemo(
    () => ({
      ...state,
      createSetting,
      deleteSetting,
      editSetting,
      getSettingById,
      copySetting,
    }),
    [state, copySetting, createSetting, deleteSetting, editSetting, getSettingById]
  );

  return <SettingsContext.Provider value={memoizedState}>{children}</SettingsContext.Provider>;
};

export { SettingsContext, SettingsProvider };
